import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Trash } from "@phosphor-icons/react";
import { lies, schick, weg } from "../api/client";
import type { Besprechung, Beteiligter, Einwaende as EinwaendeT } from "../api/types";
import { heute, datum } from "../lib/format";
import { Auswahl } from "./Auswahl";
import { Dialog, Feld, Meldung } from "./Bausteine";
import { Diktat } from "./Diktat";

/** Einwände gegen ein freigegebenes Protokoll (SPEC §4.9). Das Protokoll bleibt, wie es
 *  ist; jeder Einwand wird ein roter Punkt in der nächsten Besprechung. */
export function Einwaende({ b, beteiligte }: { b: Besprechung; beteiligte: Beteiligter[] }) {
  const qc = useQueryClient();
  const schluessel = ["einwaende", b.id];
  const daten = useQuery({ queryKey: schluessel, queryFn: () => lies<EinwaendeT>(`/besprechungen/${b.id}/einwaende`) });
  const [neu, setNeu] = useState(false);
  const kann = b.meine_rolle !== "lesen";
  const entfernen = useMutation({
    mutationFn: (id: string) => weg<EinwaendeT>(`/einwaende/${id}`),
    onSuccess: (d) => qc.setQueryData(schluessel, d),
  });
  const d = daten.data;
  if (!d || (!kann && d.einwaende.length === 0)) return null;

  return (
    <section className="karte" style={{ marginTop: 26 }}>
      <div className="karte-kopf">
        <h2>Einwände</h2>
        <span className="mute">
          {d.frist_bis ? `Frist bis ${datum(d.frist_bis)} (Zugang ${datum(d.zugang)})` : "Zugang unbekannt – nicht über das Tool versendet"}
        </span>
        <div className="luft" />
        {kann && <button className="knopf" onClick={() => setNeu(true)}><Plus size={16} weight="bold" /> Einwand</button>}
      </div>
      {d.einwaende.map((w) => (
        <div key={w.id} className="einwand">
          <div className="treffer-kopf">
            <b>{w.von}</b>
            <span className="mute">eingegangen {datum(w.eingang_am)}</span>
            {w.verspaetet && <span className="chip warn">nach Fristablauf</span>}
            <div className="luft" style={{ flex: 1 }} />
            {w.in_besprechung
              ? <Link className="chip akzent" to={`/b/${w.in_besprechung}`}>Punkt in Nr. {w.in_nummer}</Link>
              : <span className="chip">kommt in die nächste Besprechung</span>}
            {kann && !w.punkt_id && (
              <button className="knopf leise symbol" aria-label="Einwand entfernen"
                      onClick={() => confirm("Diesen Einwand entfernen?") && entfernen.mutate(w.id)}><Trash size={15} /></button>
            )}
          </div>
          <div className="einwand-text">{w.text}</div>
        </div>
      ))}
      {d.einwaende.length === 0 && <div className="leer">Keine Einwände eingegangen.</div>}
      <Meldung fehler={entfernen.error} />
      {neu && <NeuerEinwand b={b} beteiligte={beteiligte} zu={() => setNeu(false)} />}
    </section>
  );
}

function NeuerEinwand({ b, beteiligte, zu }: { b: Besprechung; beteiligte: Beteiligter[]; zu: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ beteiligter_id: "", von: "", eingang_am: heute(), eintrag_id: "", text: "" });
  const erfassen = useMutation({
    mutationFn: () => schick<EinwaendeT>(`/besprechungen/${b.id}/einwaende`, {
      ...f, beteiligter_id: f.beteiligter_id || null, eintrag_id: f.eintrag_id || null,
    }),
    onSuccess: (d) => {
      qc.setQueryData(["einwaende", b.id], d);
      // Steht schon ein Entwurf, ist der Einwand dort sofort ein Punkt.
      qc.invalidateQueries({ queryKey: ["besprechung"] });
      zu();
    },
  });
  const punkte = b.themen.flatMap((t) => t.eintraege);
  return (
    <Dialog offen zu={zu} titel={`Einwand gegen Protokoll Nr. ${b.nummer}`} fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!f.text.trim() || (!f.beteiligter_id && !f.von.trim()) || erfassen.isPending}
              onClick={() => erfassen.mutate()}>Erfassen</button>
    </>}>
      <div className="raster">
        <Feld titel="Von">
          <Auswahl value={f.beteiligter_id} onChange={(e) => setF({ ...f, beteiligter_id: e.target.value })}>
            <option value="">anderer Absender …</option>
            {beteiligte.map((x) => <option key={x.id} value={x.id}>{[x.firma, x.name].filter(Boolean).join(" · ")}</option>)}
          </Auswahl>
        </Feld>
        {!f.beteiligter_id
          ? <Feld titel="Absender"><input className="eingabe" value={f.von} onChange={(e) => setF({ ...f, von: e.target.value })} /></Feld>
          : <div />}
        <Feld titel="Eingegangen am">
          <input type="date" className="eingabe" value={f.eingang_am} onChange={(e) => setF({ ...f, eingang_am: e.target.value })} />
        </Feld>
        <Feld titel="Zu Punkt">
          <Auswahl value={f.eintrag_id} onChange={(e) => setF({ ...f, eintrag_id: e.target.value })}>
            <option value="">allgemein</option>
            {punkte.map((e) => <option key={e.id} value={e.id}>{e.nummer} {(e.titel || e.text).slice(0, 60)}</option>)}
          </Auswahl>
        </Feld>
        <div className="feld breit">
          <div className="feld-kopf"><span>Inhalt des Einwands</span>
            <Diktat text={f.text} speichern={(v) => setF((x) => ({ ...x, text: v }))} /></div>
          <textarea className="eingabe" rows={4} aria-label="Inhalt des Einwands" value={f.text}
                    onChange={(e) => setF({ ...f, text: e.target.value })} />
        </div>
      </div>
      <p className="mute">Der Einwand wird als roter Punkt in die nächste Besprechung übernommen – das freigegebene Protokoll bleibt unverändert.</p>
      <Meldung fehler={erfassen.error} />
    </Dialog>
  );
}
